import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box } from '@mui/system'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import { Button, LinearProgress } from '@mui/material'
import { courseTopicApiSlice } from '../../../../features/course/courseTopicSlice'
import { H3 } from '../../../../layouts/TextStyles'
import ActiveCourseTopicsTable from '../components/ActiveCourseTopicsTable'
import InactiveCourseTopicsTable from '../components/InactiveCourseTopicsTable'
import ReorderCourseTopicsDialog from './components/ReorderCourseTopicsDialog'
import Breadcrumb from '../../../../components/Breadcrumb'
import { capitalize } from '../../../../utils/functions'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'

const styles = {
    orderButton: {
        display: 'flex',
        color: '#4F72EA',
        alignItems: 'center',
        cursor: 'pointer',
        mr: 2,
    },
    flex: {
        display: 'flex',
        alignItems: 'center',
    },
}

const CourseTopicsPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const { courseSubjectId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')
    const [openDialog, setOpenDialog] = useState(false)
    const [activeTopicsPage, setActiveTopicsPage] = React.useState(
        +searchParams.get('activeTopicsPage') || 1,
    )
    const [activeTopicsRowsPerPage, setActiveTopicsRowsPerPage] =
        React.useState(+searchParams.get('activeTopicsSize') || DEFAULT_PAGE_SIZE)
    const [inactiveTopicsPage, setInactiveTopicsPage] = React.useState(
        +searchParams.get('inactiveTopicsPage') || 1,
    )
    const [inactiveTopicsRowsPerPage, setInactiveTopicsRowsPerPage] =
        React.useState(+searchParams.get('inactiveTopicsSize') || DEFAULT_PAGE_SIZE)

    const { data: activeCourseTopicsData = [], isFetching: isFetching } =
        courseTopicApiSlice.useFetchActiveCourseTopicsQuery(
            Number(courseSubjectId),
        )

    const {
        data: inactiveCourseTopicsData = [],
        isFetching: inactiveFetching,
    } = courseTopicApiSlice.useFetchInactiveCourseTopicsQuery(
        Number(courseSubjectId),
    )

    const [deleteCourseTopic] =
        courseTopicApiSlice.useDeleteCourseTopicMutation()

    const handleDelete = async (id: number) => {
        try {
            await deleteCourseTopic(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
        } catch (error) {}
    }

    const filteredData = () => {
        const group = searchParams.get('group')
        if (group) {
            return activeCourseTopicsData.filter(
                (item) => item.language === group,
            )
        }

        return activeCourseTopicsData
    }

    const setGroupLanguage = (lang: string) => {
        let params: any = {
            activeTopicsPage: searchParams.get('activeTopicsPage'),
            activeTopicsSize: searchParams.get('activeTopicsSize'),
            inactiveTopicsPage: searchParams.get('inactiveTopicsPage'),
            inactiveTopicsSize: searchParams.get('inactiveTopicsSize'),  
        }
        
        if(lang){
            params = {...params, group: lang}
        }

        setSearchParams(params, {state})
    }

    useEffect(() => {
        const group = searchParams.get('group')
        
        let params: any = {
            activeTopicsPage: activeTopicsPage.toString(),
            activeTopicsSize: activeTopicsRowsPerPage.toString(),
            inactiveTopicsPage: inactiveTopicsPage.toString(),
            inactiveTopicsSize: inactiveTopicsRowsPerPage.toString(),
        }

        if(group){
            params = {...params, group}
        }

        setSearchParams(params, { state })
    }, [
        activeTopicsPage,
        activeTopicsRowsPerPage,
        inactiveTopicsPage,
        inactiveTopicsRowsPerPage,
    ])

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'courses' }),
                    state?.course?.[`title${capitalize(locale)}`],
                    state?.subject?.[`title${capitalize(locale)}`],
                ]}
            />

            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'topics' })}</H3>
                    </Title>

                    <Box sx={styles.flex}>
                        <Button
                            sx={styles.orderButton}
                            endIcon={<ImportExportIcon />}
                            onClick={() => setOpenDialog(true)}
                        >
                            {formatMessage({ id: 'changeOrder' })}
                        </Button>

                        <Tabs>
                            <Tab
                                searchParams={searchParams.get('group')}
                                group="ALL"
                                onClick={() => setGroupLanguage("")}
                            >
                                {formatMessage({ id: 'allGroups' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('group')}
                                group="KZ"
                                onClick={() => setGroupLanguage('KZ')}
                            >
                                {formatMessage({ id: 'kazakhGroup' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('group')}
                                group="RU"
                                onClick={() => setGroupLanguage('RU')}
                            >
                                {formatMessage({ id: 'russianGroup' })}
                            </Tab>
                        </Tabs>
                    </Box>
                </Header>

                {isFetching && <LinearProgress />}

                {activeCourseTopicsData && (
                    <ActiveCourseTopicsTable
                        isFetching={isFetching}
                        activeTopicsPage={activeTopicsPage}
                        activeTopicsRowsPerPage={activeTopicsRowsPerPage}
                        setActiveTopicsPage={setActiveTopicsPage}
                        setActiveTopicsRowsPerPage={setActiveTopicsRowsPerPage}
                        activeCourseTopicsData={filteredData()}
                        course={state?.course}
                        subject={state?.subject}
                        onDelete={handleDelete}
                    />
                )}

                <Header style={{ marginTop: '16px' }}>
                    <Title>
                        <H3>{formatMessage({ id: 'inactiveTopics' })}</H3>
                    </Title>
                </Header>

                {inactiveFetching && <LinearProgress />}

                {inactiveCourseTopicsData && (
                    <InactiveCourseTopicsTable
                        inactiveCourseTopicsData={inactiveCourseTopicsData}
                        inactiveFetching={inactiveFetching}
                        inactiveTopicsPage={inactiveTopicsPage}
                        inactiveTopicsRowsPerPage={inactiveTopicsRowsPerPage}
                        setInactiveTopicsPage={setInactiveTopicsPage}
                        setInactiveTopicsRowsPerPage={
                            setInactiveTopicsRowsPerPage
                        }
                    />
                )}
            </Content>

            <ReorderCourseTopicsDialog
                open={openDialog}
                handleClose={() => setOpenDialog(false)}
                topics={activeCourseTopicsData}
            />
        </Wrapper>
    )
}

export default CourseTopicsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`

const Tabs = styled.div`
    display: flex;
    background: #f1f3f5;
    height: 24px;
    padding: 4px;
    border-radius: 8px;
    align-items: center;
    cursor: pointer;
`

const Tab = styled.div<{ searchParams: string; group: string }>`
    background: ${(props) =>
        props.group == 'ALL' && props.searchParams == null
            ? '#FFFFFF'
            : props.searchParams == props.group
            ? '#FFFFFF'
            : '#F1F3F5'};
    border-radius: 5px;
    padding: 4px 20px;
    color: ${(props) =>
        props.group == 'ALL' && props.searchParams == null
            ? '#404A51'
            : props.searchParams == props.group
            ? '#404A51'
            : '#AABBC7'}; ;
`
